// Tabla compacta de la lista de compra, compartida por la pestaña "Lista de
// compra" del presupuesto y la "Compra de la semana" de Compras y Almacén.
//
// Una fila baja por ingrediente: casilla "No pedir" e Ingrediente (sección o
// proveedor debajo), Necesario (paquetes editables debajo), Falta (stock y
// reservado debajo), Coste (compra por paquetes enteros, sin IVA), Estado y
// Nota. En móvil cada fila va en dos líneas: casilla, nombre y falta arriba,
// estado y nota abajo. El nombre abre la ficha del ingrediente. Pie de cada
// proveedor con el total y, al final, el total.
//
// "No pedir" (usuario, 05-oct-2026): la fila sale tachada y atenuada y no
// cuenta en los totales, el PDF ni el WhatsApp; al desmarcarla vuelve a contar.
// Cantidades editables: el número de paquetes (o la cantidad, sin paquete) es
// un input; se guarda a los 600 ms; si difiere del calculado, insignia
// "editado" y "volver a N". En los proveedores por día, un input por día.
// Pedir: en cada proveedor, la fecha de entrega (o de compra) y los botones
// "PDF del pedido" y "WhatsApp del pedido"; después ofrece marcar sus líneas
// como pedidas. El botón de crear orden queda oculto (`MOSTRAR_CREAR_ORDEN`).
//
// Props:
// - lineas, secciones ([{clave, texto}]), estados (claves de estado).
// - agrupacion: 'proveedor' | 'seccion'.
// - multievento: el origen enseña la jornada.
// - soloLectura: notas sin editar y sin "automático" (compra de la semana).
// - guardando: {clave: true} mientras se guarda una línea.
// - onCambiarEstado(linea, estado): selector de estado (insignia si la línea es
//   "No pedir" o su estado no está en `estados`), onGuardarNota(linea, nota).
// - onEstadoProveedor(grupo, 'pedido' | 'comprado'): botones "Todo pedido" y
//   "Todo comprado" en la cabecera de cada proveedor, con confirmación; promesa.
// Compra de la semana: bajo el estado, lo pedido sumando las partes
// (`textoFotoSemana`), el ajuste a propósito (`textoAjustePedido`) y lo que
// falta o sobra.
// - onNoPedir(linea, valor): casilla "No pedir".
// - onCambiarCantidad(linea, cambios): {paquetes_manual} | {cantidad_manual} |
//   {dias: [{fecha, paquetes}]} (null = volver al calculado); devuelve una promesa.
// - onPdfProveedor(grupo, fecha), onWhatsappProveedor(grupo, fecha): devuelven
//   una promesa con true si se generó; onMarcarPedidas(grupo, fecha): promesa (la fecha
//   del PDF o el WhatsApp va en la foto de lo pedido).
// - onPedir(grupo): botón "Pedir a <proveedor>" (orden de compra), solo con
//   `MOSTRAR_CREAR_ORDEN`.
// - onAbrirIngrediente(ingredienteId).
// - totalCoste, notaCoste: total de la lista y su texto de ayuda.
// - totalesProveedor: `totales_proveedor` de la respuesta ({proveedor_id: {modo_suministro,
//   pedido_por_dia, fecha_propuesta, nota_coste, ...}}): fecha propuesta, "Fecha de compra"
//   en compra directa y nota de coste del grupo en los proveedores por día.
// En los proveedores por día (líneas con `por_dia`), bajo la línea una fila pequeña con
// el desglose por día y, en el pie del grupo, "Entregas: ..." (solo en esos proveedores).
// - prefijoTestid.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MOSTRAR_CREAR_ORDEN, TEXTO_AVISO, TEXTO_ESTADO, TONO_ESTADO, NOTA_COSTE, conUnidad, costeEnCompra, cuentaEnCompra, ddmm,
  formatCantidad, formatEuros, porPaquetes, textoDia, textoCantidadDia, textoPaquetesCorto, textoUnidadInput,
  fechasEntregaLineas, textoFotoPedido, textoFotoSemana, textoEstadosPartes, textoAjustePedido,
} from './listaCompraUtils';

const claseGrid = 'grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,2.2fr)_minmax(0,1.3fr)_minmax(0,1.3fr)_minmax(0,0.9fr)_minmax(0,1.2fr)_minmax(0,1.3fr)] gap-x-3 gap-y-0.5';
const claseTh = 'text-[11px] font-bold tracking-wide uppercase text-[#5B6B64]';
const claseBotonTexto = 'text-[12px] font-semibold text-[#11704F] hover:underline';
// Móvil (uso con el dedo): botones e inputs de 40 px; en escritorio, el tamaño compacto de siempre
const claseBotonPedir = 'h-10 sm:h-[28px] px-2.5 rounded-lg border text-[13px] sm:text-[12px] font-bold whitespace-nowrap bg-white border-[#CBD3CF] text-[#16221D] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed';
const claseInputCantidad = 'h-10 w-20 text-base sm:h-[24px] sm:w-[64px] sm:text-[12px] px-1 text-right border border-[#CBD3CF] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-duetto-primary';
const claseInputFecha = 'h-10 sm:h-[28px] px-1.5 text-[12px] border border-[#CBD3CF] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-duetto-primary';
const RETARDO_GUARDADO = 600;

const textoDestino = (d) => ({
  cocina: 'cocina', bebida: 'bebida', servicio: 'servicio', barra_libre: 'barra libre',
}[d] || d || '');

// Origen de una línea agrupado por jornada, menú y plato (claves únicas)
const origenAgrupado = (origen) => {
  const mapa = new Map();
  (origen || []).forEach((o) => {
    const clave = [o.jornada_id || 'evento', o.menu || '', o.plato_id || o.plato || '', o.destino || ''].join('|');
    const previo = mapa.get(clave);
    if (previo) previo.cantidad += Number(o.cantidad) || 0;
    else mapa.set(clave, { ...o, clave, cantidad: Number(o.cantidad) || 0 });
  });
  return Array.from(mapa.values());
};

const falta = (l) => Number(l.falta_comprar) || 0;
const hay = (v) => v !== null && v !== undefined;

// Valor vigente (del servidor) y calculado del input de una línea o de un día
const valorVigente = (l, d) => {
  const fuente = d || l;
  return porPaquetes(l) ? (Number(fuente.paquetes) || 0) : (Number(fuente.cantidad_compra) || 0);
};
const valorCalculado = (l, d) => {
  if (d) return hay(d.paquetes_calculados) ? Number(d.paquetes_calculados) || 0 : null;
  const calc = porPaquetes(l) ? l.paquetes_calculados : l.cantidad_calculada;
  return hay(calc) ? Number(calc) || 0 : null;
};

const TablaListaCompra = ({
  lineas = [], secciones = [], estados = null, agrupacion = 'proveedor', multievento = false,
  soloLectura = false, guardando = {}, onCambiarEstado = null, onGuardarNota = null, onPedir = null,
  onNoPedir = null, onCambiarCantidad = null, onPdfProveedor = null, onWhatsappProveedor = null,
  onMarcarPedidas = null, onAbrirIngrediente = null, totalCoste = null, notaCoste = NOTA_COSTE,
  prefijoTestid = 'lista-compra', totalesProveedor = null, onEstadoProveedor = null,
}) => {
  const [abiertas, setAbiertas] = useState({}); // {clave: true} filas con el detalle desplegado
  const [notas, setNotas] = useState({}); // {clave: texto} notas en edición
  const [cantidades, setCantidades] = useState({}); // {clave o clave|fecha: texto} cantidades en edición
  const [fechas, setFechas] = useState({}); // {grupo: AAAA-MM-DD} fecha de entrega (o de compra) elegida
  const [ofertas, setOfertas] = useState({}); // {grupo: n} "¿Marcar estas n líneas como pedidas?"
  const [ocupados, setOcupados] = useState({}); // {grupo: true} generando PDF o WhatsApp, o marcando
  const [confirmaciones, setConfirmaciones] = useState({}); // {grupo: 'pedido' | 'comprado'} "¿Marcar las N líneas...?"
  const temporizadores = useRef({});
  const fechasOferta = useRef({}); // {grupo: fecha} del último PDF o WhatsApp del pedido

  useEffect(() => {
    const t = temporizadores.current;
    return () => Object.values(t).forEach(clearTimeout);
  }, []);

  const textoSeccion = useMemo(() => Object.fromEntries((secciones || []).map(s => [s.clave, s.texto])), [secciones]);

  const grupos = useMemo(() => {
    const mapa = new Map();
    lineas.forEach((l) => {
      const clave = agrupacion === 'proveedor' ? (l.proveedor_id || 'sin-proveedor') : (l.seccion || 'secos');
      const titulo = agrupacion === 'proveedor' ? (l.proveedor_nombre || 'Sin proveedor') : (textoSeccion[l.seccion] || l.seccion);
      if (!mapa.has(clave)) {
        mapa.set(clave, {
          clave, titulo, proveedorId: agrupacion === 'proveedor' ? (l.proveedor_id || null) : null,
          lineas: [], totalCoste: 0, sinPrecio: 0, pedibles: 0, enPedido: 0,
        });
      }
      const g = mapa.get(clave);
      g.lineas.push(l);
      if (cuentaEnCompra(l)) {
        g.totalCoste += costeEnCompra(l);
        g.enPedido += 1;
        if (l.sin_precio) g.sinPrecio += 1;
      }
      if (l.pedible) g.pedibles += 1;
    });
    const lista = Array.from(mapa.values());
    if (agrupacion === 'seccion') {
      const orden = (secciones || []).map(s => s.clave);
      lista.sort((a, b) => orden.indexOf(a.clave) - orden.indexOf(b.clave));
    } else {
      lista.sort((a, b) => (a.clave === 'sin-proveedor') - (b.clave === 'sin-proveedor') || a.titulo.localeCompare(b.titulo, 'es'));
    }
    return lista;
  }, [lineas, agrupacion, secciones, textoSeccion]);

  const total = totalCoste !== null && totalCoste !== undefined
    ? Number(totalCoste) || 0
    : grupos.reduce((s, g) => s + g.totalCoste, 0);

  const guardarNota = (l) => {
    const texto = notas[l.clave];
    setNotas((prev) => {
      const r = { ...prev };
      delete r[l.clave];
      return r;
    });
    if (texto === undefined || (texto || '') === (l.nota || '') || !onGuardarNota) return;
    onGuardarNota(l, texto);
  };

  const quitarEdicion = (clave) => setCantidades((prev) => {
    const r = { ...prev };
    delete r[clave];
    return r;
  });

  // Guarda la cantidad (con retardo) y quita la edición cuando el servidor responde
  const guardarCantidad = (l, d, clave, texto) => {
    delete temporizadores.current[clave];
    const n = Number(String(texto).replace(',', '.'));
    if (String(texto).trim() === '' || Number.isNaN(n) || n < 0 || !onCambiarCantidad) {
      quitarEdicion(clave);
      return;
    }
    const valor = porPaquetes(l) ? Math.round(n) : n;
    let cambios;
    if (d) cambios = { dias: [{ fecha: d.fecha || null, paquetes: valor }] };
    else cambios = porPaquetes(l) ? { paquetes_manual: valor } : { cantidad_manual: valor };
    Promise.resolve(onCambiarCantidad(l, cambios)).finally(() => quitarEdicion(clave));
  };

  const cambiarCantidad = (l, d, clave, texto) => {
    setCantidades(prev => ({ ...prev, [clave]: texto }));
    clearTimeout(temporizadores.current[clave]);
    temporizadores.current[clave] = setTimeout(() => guardarCantidad(l, d, clave, texto), RETARDO_GUARDADO);
  };

  const volver = (l, d) => {
    if (!onCambiarCantidad) return;
    if (d) onCambiarCantidad(l, { dias: [{ fecha: d.fecha || null, paquetes: null }] });
    else onCambiarCantidad(l, porPaquetes(l) ? { paquetes_manual: null } : { cantidad_manual: null });
  };

  // Input de paquetes (o cantidad) con su unidad, insignia "editado" y "volver a N"
  const renderCantidad = (l, d = null) => {
    const clave = d ? `${l.clave}|${d.fecha || ''}` : l.clave;
    const valor = cantidades[clave] !== undefined ? cantidades[clave] : valorVigente(l, d);
    const fuente = d || l;
    const calculado = valorCalculado(l, d);
    const editado = !!fuente.editado && calculado !== null && Number(valorVigente(l, d)) !== calculado;
    const testid = d ? `${prefijoTestid}-paquetes-${l.ingrediente_id}-${d.fecha || 'sin-fecha'}` : `${prefijoTestid}-paquetes-${l.ingrediente_id}`;
    return (
      <span className="inline-flex flex-wrap items-center justify-end gap-1 align-middle leading-none text-[11px] text-[#5B6B64]">
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step={porPaquetes(l) ? 1 : 0.01}
          value={valor}
          onChange={(e) => cambiarCantidad(l, d, clave, e.target.value)}
          disabled={!!guardando[l.clave]}
          className={claseInputCantidad}
          aria-label={`${porPaquetes(l) ? 'Paquetes' : 'Cantidad'} de ${l.nombre}${d ? ` el ${textoDia(d.fecha)}` : ''}`}
          data-testid={testid}
        />
        <span>{textoUnidadInput(l)}</span>
        {editado && (
          <>
            <span className="px-1 rounded font-bold bg-[#FDF0DC] text-[#92400E]" data-testid={`${testid}-editado`}>editado</span>
            <button type="button" onClick={() => volver(l, d)} className="font-semibold text-[#11704F] hover:underline" data-testid={`${testid}-volver`}>
              volver a {formatCantidad(calculado)}
            </button>
          </>
        )}
      </span>
    );
  };

  // Chip de lo que falta o sobra sobre lo ya pedido (solo pedido o comprado)
  const renderDiferencia = (l) => {
    if (!['pedido', 'comprado'].includes(l.estado)) return null;
    const enPaquetes = porPaquetes(l) && hay(l.diferencia_paquetes);
    const dif = enPaquetes ? Number(l.diferencia_paquetes) : Number(l.diferencia);
    if (!Number.isFinite(dif) || dif === 0) return null;
    const n = Math.abs(dif);
    const testid = `${prefijoTestid}-diferencia-${l.ingrediente_id}`;
    if (dif > 0) {
      const texto = enPaquetes ? `${n === 1 ? 'falta' : 'faltan'} ${formatCantidad(n)} paq` : `falta ${conUnidad(n, l.unidad)}`;
      return (
        <span className="px-1 py-px rounded text-[11px] font-bold bg-[#FDF0DC] text-[#92400E]" title="Sobre lo ya pedido. Usa Solo lo que falta o el aviso para pedirlo" data-testid={testid}>
          {texto}
        </span>
      );
    }
    const texto = enPaquetes ? `${n === 1 ? 'sobra' : 'sobran'} ${formatCantidad(n)} paq` : `sobra ${conUnidad(n, l.unidad)}`;
    return (
      <span className="px-1 py-px rounded text-[11px] font-bold bg-[#EEF1EF] text-[#4A5A53]" title="Sobre lo ya pedido" data-testid={testid}>
        {texto}
      </span>
    );
  };

  const renderEstado = (l) => {
    const tono = TONO_ESTADO[l.estado] || '';
    const opciones = estados || Object.keys(TEXTO_ESTADO);
    const editable = !!onCambiarEstado && !l.no_pedir && opciones.includes(l.estado);
    // Compra de la semana: la línea suma las fotos de sus partes
    const deSemana = l.estados_partes !== undefined || l.ultimo_pedido_en !== undefined;
    const foto = deSemana ? textoFotoSemana(l) : textoFotoPedido(l);
    const tituloFoto = deSemana
      ? (textoEstadosPartes(l.estados_partes) || undefined)
      : (l.pedido_fecha_entrega ? `Entrega ${String(l.pedido_fecha_entrega).slice(8, 10)}/${String(l.pedido_fecha_entrega).slice(5, 7)}` : undefined);
    return (
      <div className="flex flex-wrap items-center gap-1">
        {!editable ? (
          <span className={`px-1.5 py-0.5 rounded text-[12px] font-bold ${tono}`} data-testid={`${prefijoTestid}-estado-${l.ingrediente_id}`}>
            {TEXTO_ESTADO[l.estado] || l.estado}
          </span>
        ) : (
          <>
            <div className="sm:hidden flex gap-1 w-full basis-full" role="group" aria-label={`Estado de ${l.nombre}`} data-testid={`${prefijoTestid}-estado-movil-${l.ingrediente_id}`} data-tour="compras-estado">
              {opciones.map((e) => {
                const activo = e === l.estado;
                return (
                  <button
                    key={e}
                    type="button"
                    onClick={() => { if (!activo) onCambiarEstado(l, e); }}
                    data-uso="compras.estado"
                    data-uso-detalle={e}
                    disabled={!!guardando[l.clave]}
                    aria-pressed={activo}
                    className={`flex-1 min-w-0 h-10 px-1 rounded-lg border text-[13px] truncate disabled:opacity-60 ${
                      activo ? `${TONO_ESTADO[e] || ''} border-current font-bold` : 'bg-white border-[#E1E6E3] text-[#5B6B64] font-semibold'
                    }`}
                    data-testid={`${prefijoTestid}-estado-movil-${l.ingrediente_id}-${e}`}
                  >
                    {TEXTO_ESTADO[e] || e}
                  </button>
                );
              })}
            </div>
            <select
              value={l.estado}
              onChange={(e) => onCambiarEstado(l, e.target.value)}
              data-uso="compras.estado"
              data-uso-detalle="selector"
              disabled={!!guardando[l.clave]}
              className={`hidden sm:block h-10 text-base sm:h-[26px] sm:text-[12px] px-1 border border-[#CBD3CF] rounded-md focus:outline-none focus:ring-2 focus:ring-duetto-primary ${tono}`}
              aria-label={`Estado de ${l.nombre}`}
              data-testid={`${prefijoTestid}-estado-${l.ingrediente_id}`}
              data-tour="compras-estado"
            >
              {opciones.map(e => (
                <option key={e} value={e}>{TEXTO_ESTADO[e] || e}</option>
              ))}
            </select>
          </>
        )}
        {l.orden_numero && (
          <span className="px-1 py-px rounded text-[11px] font-bold bg-[#E7EEFB] text-[#1E3A8A]" title="Orden de compra" data-testid={`${prefijoTestid}-orden-${l.ingrediente_id}`}>
            Pedido {l.orden_numero}
          </span>
        )}
        {foto && (
          <span className="text-[11px] text-[#5B6B64]" title={tituloFoto} data-testid={`${prefijoTestid}-foto-${l.ingrediente_id}`}>
            {foto}
          </span>
        )}
        {textoAjustePedido(l) && (
          <span className="text-[11px] text-[#5B6B64]" title="Diferencia entre lo pedido y lo que hacía falta al marcar. No cambia lo que falta para eventos nuevos" data-testid={`${prefijoTestid}-ajuste-${l.ingrediente_id}`}>
            {foto ? '· ' : ''}{textoAjustePedido(l)}
          </span>
        )}
        {renderDiferencia(l)}
        {!soloLectura && l.estado_guardado === false && <span className="text-[11px] text-[#5B6B64]">automático</span>}
      </div>
    );
  };

  const renderNota = (l) => {
    const editando = notas[l.clave] !== undefined;
    if (soloLectura || !onGuardarNota) {
      return <span className="text-[12px] text-[#4A5A53] truncate block" title={l.nota || ''}>{l.nota || ''}</span>;
    }
    if (editando) {
      return (
        <input
          type="text"
          autoFocus
          value={notas[l.clave]}
          onChange={(e) => setNotas(prev => ({ ...prev, [l.clave]: e.target.value }))}
          onBlur={() => guardarNota(l)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') setNotas((prev) => { const r = { ...prev }; delete r[l.clave]; return r; });
          }}
          maxLength={500}
          placeholder="Nota"
          className="w-full h-10 sm:h-[26px] px-1.5 text-[12px] border border-[#CBD3CF] rounded-md focus:outline-none focus:ring-2 focus:ring-duetto-primary"
          aria-label={`Nota de ${l.nombre}`}
          data-testid={`${prefijoTestid}-nota-input-${l.ingrediente_id}`}
        />
      );
    }
    return (
      <button
        type="button"
        onClick={() => setNotas(prev => ({ ...prev, [l.clave]: l.nota || '' }))}
        disabled={!!guardando[l.clave]}
        className={`py-1.5 px-1 inline-block ${l.nota ? 'text-[12px] text-[#16221D] text-left truncate max-w-full hover:underline' : claseBotonTexto}`}
        title={l.nota || 'Añadir nota'}
        data-testid={`${prefijoTestid}-nota-${l.ingrediente_id}`}
      >
        {l.nota || 'Nota'}
      </button>
    );
  };

  const renderDetalle = (l) => {
    const origen = origenAgrupado(l.origen);
    return (
      <div className="px-2 py-1.5 bg-[#F7F9F8] border-b border-[#EEF1EF]">
        <ul className="text-[12px] text-[#4A5A53] flex flex-col gap-0.5">
          {(l.desglose || []).map((d, i) => (
            <li key={`${d.presupuesto_id}-${d.jornada_id || 'evento'}-${i}`}>
              Presupuesto {d.numero}{d.etiqueta ? ` · ${d.etiqueta}` : ''}{d.fecha ? ` · ${ddmm(d.fecha)}` : ''}
              {d.nombre_evento ? ` · ${d.nombre_evento}` : ''}: <span className="font-semibold">{conUnidad(d.cantidad, l.unidad)}</span>
              {' · '}{TEXTO_ESTADO[d.estado] || d.estado}{d.orden_numero ? ` (${d.orden_numero})` : ''}
              {d.no_pedir ? ' · No pedir' : ''}
            </li>
          ))}
          {origen.map(o => (
            <li key={o.clave}>
              {multievento && o.jornada ? `${o.jornada} · ` : ''}
              {o.menu} · {o.plato}
              {o.sustituye_a ? ` (sustituye a ${o.sustituye_a})` : ''}
              {' · '}{textoDestino(o.destino)}
              {o.uds_por_persona !== undefined && o.uds_catalogo !== undefined && Number(o.uds_por_persona) !== Number(o.uds_catalogo)
                ? ` · ${formatCantidad(o.uds_por_persona)} por persona en la ficha (catálogo ${formatCantidad(o.uds_catalogo)})`
                : ''}
              {': '}<span className="font-semibold">{conUnidad(o.cantidad, l.unidad)}</span>
            </li>
          ))}
          {Number(l.merma_pct) > 0 && <li>Receta {conUnidad(l.cantidad_receta, l.unidad)} + {formatCantidad(l.merma_pct)} % de merma</li>}
          {l.texto_paquetes && <li>Paquetes: {l.texto_paquetes}</li>}
          {(l.avisos || []).map(a => <li key={`aviso-${a.tipo}`} className="text-[#92400E]">{a.texto}</li>)}
          {(l.lotes || []).length > 0 && (
            <li>
              Lotes: {l.lotes.map(x => `${x.numero_lote || 'sin número'} (${x.caducidad || 'sin caducidad'}${x.reservado ? ', reservado' : ''}): ${formatCantidad(x.cantidad)}`).join(' · ')}
            </li>
          )}
        </ul>
      </div>
    );
  };

  const renderPorDia = (l) => {
    const editable = !!onCambiarCantidad && !l.no_pedir;
    return (
      <div className={`px-2 pb-1 -mt-px text-[11px] text-[#4A5A53] border-b border-[#EEF1EF] ${l.no_pedir ? 'line-through opacity-50' : ''}`} data-testid={`${prefijoTestid}-por-dia-${l.ingrediente_id}`}>
        <span className="font-semibold text-[#0F5E43]">Por día:</span>{' '}
        {editable ? (
          <span className="inline-flex flex-wrap gap-x-3 gap-y-1 align-middle">
            {l.por_dia.map(d => (
              <span key={d.fecha || 'sin-fecha'} className="inline-flex items-center gap-1">
                <span className="font-semibold text-[#16221D]">{textoDia(d.fecha)}:</span>
                {renderCantidad(l, d)}
              </span>
            ))}
          </span>
        ) : (
          l.por_dia.map(d => `${textoDia(d.fecha)}: ${textoCantidadDia(l, d)}`).join(' · ')
        )}
      </div>
    );
  };

  const renderFila = (l) => {
    const abierta = !!abiertas[l.clave];
    const subtitulo = agrupacion === 'proveedor' ? (textoSeccion[l.seccion] || l.seccion) : l.proveedor_nombre;
    const f = falta(l);
    const conCoste = f > 0 || !!l.manual;
    const paquetes = textoPaquetesCorto(l);
    const porDia = !!(l.por_dia && l.por_dia.length > 0);
    const editable = !!onCambiarCantidad && !l.no_pedir && !porDia;
    const partesStock = [`stock ${formatCantidad(l.stock_disponible)}`, `reservado ${formatCantidad(l.reservado_lotes)}`];
    if (Number(l.pedido_en_ordenes) > 0) partesStock.push(`pedido ${formatCantidad(l.pedido_en_ordenes)}`);
    const tachada = l.no_pedir ? 'line-through' : '';
    return (
      <React.Fragment key={l.clave}>
        <div
          className={`${claseGrid} px-2 py-1.5 sm:py-1 text-[13px] border-b border-[#EEF1EF] items-center ${l.no_pedir ? 'bg-[#F7F9F8]' : ''}`}
          data-testid={`${prefijoTestid}-fila-${l.ingrediente_id}`}
          data-no-pedir={l.no_pedir ? 'si' : 'no'}
        >
          <div className="min-w-0 flex items-start gap-2">
            {onNoPedir && (
              <label className="flex flex-col items-center shrink-0 pt-0.5 cursor-pointer" title="No pedir: no se pide ni cuenta en los totales" data-tour="compras-no-pedir">
                <input
                  type="checkbox"
                  checked={!!l.no_pedir}
                  onChange={(e) => onNoPedir(l, e.target.checked)}
                  disabled={!!guardando[l.clave]}
                  className="h-5 w-5 sm:h-4 sm:w-4 accent-[#8A1C12]"
                  aria-label={`No pedir ${l.nombre}`}
                  data-testid={`${prefijoTestid}-no-pedir-${l.ingrediente_id}`}
                />
                <span className="block text-[10px] sm:text-[9px] leading-tight text-[#5B6B64] whitespace-nowrap">No pedir</span>
              </label>
            )}
            <div className={`min-w-0 ${l.no_pedir ? 'opacity-50' : ''}`}>
              {onAbrirIngrediente ? (
                <button
                  type="button"
                  onClick={() => onAbrirIngrediente(l.ingrediente_id)}
                  className={`font-semibold text-[#16221D] hover:underline text-left truncate max-w-full block ${tachada}`}
                  title="Abrir la ficha del ingrediente"
                  data-testid={`${prefijoTestid}-ingrediente-${l.ingrediente_id}`}
                >
                  {l.nombre}
                </button>
              ) : (
                <span className={`font-semibold text-[#16221D] truncate block ${tachada}`}>{l.nombre}</span>
              )}
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-[#5B6B64]">
                {subtitulo && <span className="truncate">{subtitulo}</span>}
                {l.no_pedir && <span className="px-1 rounded font-bold bg-[#EEF1EF] text-[#4A5A53]">No pedir</span>}
                {(l.avisos || []).map(a => (
                  <span
                    key={a.tipo}
                    title={a.texto}
                    className={`px-1 rounded font-bold ${a.tipo === 'compartido' || a.tipo === 'ya_pedido' ? 'bg-[#E7EEFB] text-[#1E3A8A]' : 'bg-[#FDF0DC] text-[#92400E]'}`}
                  >
                    {TEXTO_AVISO[a.tipo] || a.tipo}
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => setAbiertas(prev => ({ ...prev, [l.clave]: !prev[l.clave] }))}
                  className="py-1.5 px-1 inline-block font-semibold text-[#11704F] hover:underline"
                  aria-expanded={abierta}
                  data-testid={`${prefijoTestid}-origen-${l.ingrediente_id}`}
                >
                  {abierta ? 'Ocultar' : (l.desglose ? 'Eventos' : 'Origen')}
                </button>
              </div>
              {/* Móvil: la columna Necesario está oculta, así que lo necesario y los paquetes van aquí */}
              <div className={`sm:hidden mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-[#5B6B64] tabular-nums ${tachada}`} data-testid={`${prefijoTestid}-necesario-movil-${l.ingrediente_id}`}>
                <span>Necesario {conUnidad(l.cantidad_con_merma, l.unidad)}</span>
                {editable ? renderCantidad(l) : (paquetes && <span>{paquetes}</span>)}
              </div>
            </div>
          </div>
          <div className={`hidden sm:flex flex-col items-end justify-center gap-0.5 text-right tabular-nums ${l.no_pedir ? 'opacity-50' : ''}`}>
            <span className={tachada}>{conUnidad(l.cantidad_con_merma, l.unidad)}</span>
            {editable ? (
              renderCantidad(l)
            ) : (
              paquetes && <div className={`text-[11px] text-[#5B6B64] ${tachada}`}>{paquetes}</div>
            )}
          </div>
          <div className={`text-right tabular-nums ${l.no_pedir ? 'opacity-50' : ''}`}>
            <span className={`font-bold ${f > 0 ? 'text-[#8A1C12]' : 'text-[#0F5E43]'} ${tachada}`}>{conUnidad(f, l.unidad)}</span>
            <div className={`text-[11px] text-[#5B6B64] sm:whitespace-nowrap ${tachada}`}>{partesStock.join(' · ')}</div>
            <div className={`sm:hidden text-[11px] text-[#5B6B64] ${tachada}`}>
              {conCoste ? (l.sin_precio ? 'sin precio' : formatEuros(l.coste_estimado)) : ''}
            </div>
          </div>
          <div className={`hidden sm:block text-right tabular-nums ${l.no_pedir ? 'opacity-50 line-through' : ''}`} data-testid={`${prefijoTestid}-coste-${l.ingrediente_id}`}>
            {conCoste ? (l.sin_precio ? <span className="text-[12px] text-[#9AA6A0]">sin precio</span> : formatEuros(l.coste_estimado)) : <span className="text-[#9AA6A0]">-</span>}
            {conCoste && !l.sin_precio && l.tipo_iva ? <div className="text-[11px] text-[#5B6B64]">IVA {l.tipo_iva} %</div> : null}
          </div>
          <div className={`min-w-0 col-span-2 sm:col-span-1 ${l.no_pedir ? 'opacity-50' : ''}`}>{renderEstado(l)}</div>
          <div className={`min-w-0 col-span-2 sm:col-span-1 text-right sm:text-left ${l.no_pedir ? 'opacity-50' : ''}`}>{renderNota(l)}</div>
        </div>
        {porDia && renderPorDia(l)}
        {abierta && renderDetalle(l)}
      </React.Fragment>
    );
  };

  // PDF o WhatsApp del pedido del proveedor; después ofrece marcar sus líneas como pedidas
  const generarPedido = async (g, tipo, fecha) => {
    const accion = tipo === 'pdf' ? onPdfProveedor : onWhatsappProveedor;
    if (!accion) return;
    setOcupados(prev => ({ ...prev, [g.clave]: true }));
    try {
      const ok = await accion(g, fecha || null);
      fechasOferta.current[g.clave] = fecha || null;
      if (ok && onMarcarPedidas && g.pedibles > 0) setOfertas(prev => ({ ...prev, [g.clave]: g.pedibles }));
    } finally {
      setOcupados(prev => ({ ...prev, [g.clave]: false }));
    }
  };

  const cerrarOferta = (clave) => setOfertas((prev) => {
    const r = { ...prev };
    delete r[clave];
    return r;
  });

  const marcarPedidas = async (g) => {
    setOcupados(prev => ({ ...prev, [g.clave]: true }));
    try {
      await onMarcarPedidas(g, fechasOferta.current[g.clave] || null);
    } finally {
      setOcupados(prev => ({ ...prev, [g.clave]: false }));
      cerrarOferta(g.clave);
    }
  };

  // "Todo pedido" / "Todo comprado": líneas del grupo (sin "No pedir") cuyo estado cambiaría
  const lineasACambiar = (g, estado) => g.lineas.filter(l => !l.no_pedir && (
    estado === 'pedido' ? l.estado === 'pendiente' : ['pendiente', 'pedido'].includes(l.estado)
  )).length;

  const cerrarConfirmacion = (clave) => setConfirmaciones((prev) => {
    const r = { ...prev };
    delete r[clave];
    return r;
  });

  const confirmarEstadoProveedor = async (g, estado) => {
    setOcupados(prev => ({ ...prev, [g.clave]: true }));
    try {
      await onEstadoProveedor(g, estado);
    } finally {
      setOcupados(prev => ({ ...prev, [g.clave]: false }));
      cerrarConfirmacion(g.clave);
    }
  };

  const renderPedirProveedor = (g, info) => {
    const compraDirecta = info.modo_suministro === 'compra_directa';
    const porDia = !!info.pedido_por_dia;
    const fecha = fechas[g.clave] !== undefined ? fechas[g.clave] : (info.fecha_propuesta || '');
    const sinNada = g.enPedido === 0;
    const ocupado = !!ocupados[g.clave];
    const titulo = sinNada ? 'No hay nada que pedir a este proveedor' : '';
    return (
      <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:flex-wrap sm:items-center sm:gap-1.5 sm:w-auto" data-testid={`${prefijoTestid}-pedir-${g.clave}`}>
        {!porDia && (
          <label className="col-span-2 flex items-center gap-1 text-[12px] text-[#4A5A53] [&>input]:flex-1 sm:[&>input]:flex-none">
            {compraDirecta ? 'Fecha de compra' : 'Fecha de entrega'}
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFechas(prev => ({ ...prev, [g.clave]: e.target.value }))}
              className={claseInputFecha}
              aria-label={`${compraDirecta ? 'Fecha de compra' : 'Fecha de entrega'} de ${g.titulo}`}
              data-testid={`${prefijoTestid}-fecha-${g.clave}`}
            />
          </label>
        )}
        {onPdfProveedor && (
          <button
            type="button"
            onClick={() => generarPedido(g, 'pdf', porDia ? null : fecha)}
            data-uso="compras.pdf"
            disabled={sinNada || ocupado}
            className={claseBotonPedir}
            title={titulo || (compraDirecta ? `Lista de compra en ${g.titulo} en PDF` : `Pedido a ${g.titulo} en PDF`)}
            data-testid={`${prefijoTestid}-pdf-${g.clave}`}
            data-tour="compras-pdf"
          >
            PDF del pedido
          </button>
        )}
        {onWhatsappProveedor && (
          <button
            type="button"
            onClick={() => generarPedido(g, 'whatsapp', porDia ? null : fecha)}
            data-uso="compras.whatsapp"
            disabled={sinNada || ocupado}
            className={claseBotonPedir}
            title={titulo || `Copiar el texto del pedido a ${g.titulo} para WhatsApp`}
            data-testid={`${prefijoTestid}-whatsapp-${g.clave}`}
            data-tour="compras-whatsapp"
          >
            WhatsApp del pedido
          </button>
        )}
        {onEstadoProveedor && [['pedido', 'Todo pedido', 'todo-pedido'], ['comprado', 'Todo comprado', 'todo-comprado']].map(([estado, texto, sufijo]) => {
          const n = lineasACambiar(g, estado);
          return (
            <button
              key={estado}
              type="button"
              onClick={() => setConfirmaciones(prev => ({ ...prev, [g.clave]: estado }))}
              data-uso="compras.todo_pedido"
              data-uso-detalle={estado}
              disabled={n === 0 || ocupado}
              className={claseBotonPedir}
              title={n === 0 ? 'Nada que marcar' : `Marcar las líneas de ${g.titulo} como ${estado === 'pedido' ? 'pedidas' : 'compradas'}`}
              data-testid={`${prefijoTestid}-${sufijo}-${g.clave}`}
              data-tour={estado === 'pedido' ? 'compras-todo-pedido' : undefined}
            >
              {texto}
            </button>
          );
        })}
      </div>
    );
  };

  // Confirmación inline de "Todo pedido" / "Todo comprado"
  const renderConfirmacionEstado = (g) => {
    const estado = confirmaciones[g.clave];
    if (!estado || !onEstadoProveedor) return null;
    const n = lineasACambiar(g, estado);
    const como = estado === 'pedido' ? (n === 1 ? 'pedida' : 'pedidas') : (n === 1 ? 'comprada' : 'compradas');
    const pregunta = n === 1 ? `¿Marcar la línea de ${g.titulo} como ${como}?` : `¿Marcar las ${n} líneas de ${g.titulo} como ${como}?`;
    return (
      <div className="flex flex-wrap items-center justify-end gap-2 rounded-lg bg-[#E7EEFB] px-2.5 py-1.5 text-[12px] text-[#1E3A8A]" data-testid={`${prefijoTestid}-confirmar-estado-${g.clave}`}>
        <span>{pregunta}</span>
        <button
          type="button"
          onClick={() => confirmarEstadoProveedor(g, estado)}
          data-uso="compras.todo_pedido.confirmar"
          data-uso-detalle={estado}
          disabled={!!ocupados[g.clave] || n === 0}
          className={claseBotonPedir}
          data-testid={`${prefijoTestid}-confirmar-estado-si-${g.clave}`}
        >
          Sí
        </button>
        <button type="button" onClick={() => cerrarConfirmacion(g.clave)} className={claseBotonPedir} data-testid={`${prefijoTestid}-confirmar-estado-no-${g.clave}`}>No</button>
      </div>
    );
  };

  if (lineas.length === 0) return null;

  return (
    <div className="flex flex-col gap-4" data-testid={`${prefijoTestid}-tabla`}>
      {grupos.map(g => {
        const info = (agrupacion === 'proveedor' && g.proveedorId && totalesProveedor) ? (totalesProveedor[g.proveedorId] || {}) : {};
        const compraDirecta = info.modo_suministro === 'compra_directa';
        const entregas = agrupacion === 'proveedor' ? fechasEntregaLineas(g.lineas) : [];
        const conProveedor = agrupacion === 'proveedor' && !!g.proveedorId;
        const oferta = ofertas[g.clave];
        return (
        <div key={g.clave} className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-extrabold text-[#0F5E43]" data-tour="compras-proveedor">
              {g.titulo} <span className="font-semibold text-[#5B6B64]">· {g.lineas.length}</span>
            </h3>
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              {conProveedor && (onPdfProveedor || onWhatsappProveedor || onEstadoProveedor) && renderPedirProveedor(g, info)}
              {MOSTRAR_CREAR_ORDEN && onPedir && conProveedor && (
                <button
                  type="button"
                  onClick={() => onPedir(g)}
                  data-uso="compras.pedir_proveedor"
                  disabled={g.pedibles === 0}
                  className={claseBotonPedir}
                  title={g.pedibles === 0 ? 'No falta nada por pedir a este proveedor' : (compraDirecta ? `Crear una lista para comprar en ${g.titulo}` : `Crear una orden de compra para ${g.titulo}`)}
                  data-testid={`${prefijoTestid}-crear-orden-${g.clave}`}
                >
                  {compraDirecta ? `Lista para comprar en ${g.titulo}` : `Pedir a ${g.titulo}`}
                </button>
              )}
            </div>
          </div>
          {oferta > 0 && (
            <div className="flex flex-wrap items-center justify-end gap-2 rounded-lg bg-[#E7EEFB] px-2.5 py-1.5 text-[12px] text-[#1E3A8A]" data-testid={`${prefijoTestid}-marcar-${g.clave}`}>
              <span>¿Marcar {oferta === 1 ? 'esta línea' : `estas ${oferta} líneas`} como pedidas?</span>
              <button type="button" onClick={() => marcarPedidas(g)} disabled={!!ocupados[g.clave]} className={claseBotonPedir} data-testid={`${prefijoTestid}-marcar-si-${g.clave}`} data-uso="compras.marcar_pedidas">
                Marcar como pedidas
              </button>
              <button type="button" onClick={() => cerrarOferta(g.clave)} className={claseBotonPedir}>No</button>
            </div>
          )}
          {conProveedor && renderConfirmacionEstado(g)}
          <div className="overflow-x-auto">
            <div className="sm:min-w-[600px]" data-testid={`${prefijoTestid}-grupo-${g.clave}`}>
              <div className={`${claseGrid} hidden sm:grid px-2 py-1 border-b border-[#E1E6E3]`}>
                <span className={claseTh}>Ingrediente</span>
                <span className={`${claseTh} text-right`}>Necesario</span>
                <span className={`${claseTh} text-right`}>Falta</span>
                <span className={`${claseTh} text-right`}>Coste</span>
                <span className={claseTh}>Estado</span>
                <span className={claseTh}>Nota</span>
              </div>
              {g.lineas.map(renderFila)}
              <div className="flex flex-wrap justify-end gap-x-3 px-2 py-1 text-[13px]" data-testid={`${prefijoTestid}-total-${g.clave}`}>
                {g.sinPrecio > 0 && <span className="text-[12px] text-[#9AA6A0]">{g.sinPrecio} sin precio</span>}
                {entregas.length > 0 && (
                  <span className="text-[12px] text-[#4A5A53]" data-testid={`${prefijoTestid}-entregas-${g.clave}`}>Entregas: {entregas.map(ddmm).join(', ')}</span>
                )}
                <span className="font-bold text-[#16221D]">Total {g.titulo}: {formatEuros(g.totalCoste)} <span className="font-normal text-[#5B6B64]">sin IVA</span></span>
                {info.nota_coste && <span className="basis-full text-right text-[11px] text-[#5B6B64]">{info.nota_coste}</span>}
              </div>
            </div>
          </div>
        </div>
        );
      })}
      <div className="flex flex-col items-end gap-0.5 border-t border-[#E1E6E3] pt-2" data-testid={`${prefijoTestid}-total`}>
        <span className="text-sm font-extrabold text-[#0F5E43]">Total de la lista: {formatEuros(total)} <span className="font-normal text-[#5B6B64]">sin IVA</span></span>
        <span className="text-[11px] text-[#5B6B64]">{notaCoste || NOTA_COSTE}</span>
      </div>
    </div>
  );
};

export default TablaListaCompra;
